class SubscriptionSelector extends HTMLElement {
  constructor() {
    super();
    this.currentVariantId = null;
  }

  connectedCallback() {
    this.subscriptionForm = this.querySelector(".subscription-form");
    this.subscriptionForm.addEventListener("submit", (e) => {
      e.preventDefault();
      this.handleAddToCart(e);
    });

    this.paymentTypeToggle = this.querySelector("#payment-type-toggle");
    this.addToCartButton = this.querySelector("#add-to-cart-subscription");
    this.unavailableButton = this.querySelector("#unavailable-link");
    this.priceElement = this.querySelector(".product__selling-plan-price");
    this.savingsValue = this.querySelector(
      ".product__selling-plan-savings-value"
    );
    this.unavailableMessage = this.querySelector(
      ".product__selling-plan-unavailable-message"
    );
    this.priceWrap = this.querySelector(".product__selling-plan-price-wrap");
    this.originalPrice = this.querySelector(
      ".product__selling-plan-original-price"
    );
    this.cancelElement = this.querySelector(".product__selling-plan-cancel");

    // No need to store original button text since we swap buttons now

    // Payment option containers
    this.onetimeOptions = this.querySelector(".onetime-payment-options");
    this.subscriptionOptions = this.querySelector(
      ".subscription-payment-options"
    );

    // Text-Elemente für verschiedene Abo-Typen
    this.multipleMonths = this.querySelector("[data-months]");
    this.singleMonth = this.querySelector("[data-month]");
    this.onetimePurchase = this.querySelector("[data-onetime]");

    this.threshold = 0;

    this.initializeEventListeners();
    this.initializeDefaultState();

    // Listen für URL-Änderungen (z.B. durch History API)
    window.addEventListener("popstate", () => {
      this.handleUrlChange();
    });
  }

  initializeEventListeners() {
    // Event Listener für Selling Plan Änderungen
    this.subscriptionForm.addEventListener("change", (e) => {
      if (e.target.name === "selling_plan") {
        this.handleSellingPlanChange(e);
      }
    });
    // Wenn Dropdown existiert, hänge separaten Listener an
    const dropdown = this.subscriptionForm.querySelector(
      "#selling-plan-select"
    );
    if (dropdown) {
      dropdown.addEventListener("change", (e) => {
        this.handleSellingPlanChange(e);
      });
    }

    // Event Listener für Payment Type Toggle (Einmalzahlung/Abo)
    if (this.paymentTypeToggle) {
      this.paymentTypeToggle.addEventListener(
        "change",
        this.handlePaymentTypeChange.bind(this)
      );
    }

    // Event Listener für Add to Cart (only if button exists)
    if (this.addToCartButton) {
      this.addToCartButton.addEventListener("click", (e) =>
        this.handleAddToCart(e)
      );
    }

    // Event Listener für Frequenz-Updates
    this.updateFrequencyLabels();
  }

  initializeDefaultState() {
    // Prüfe URL-Parameter für Varianten-Vorauswahl (hat höchste Priorität)
    const urlParams = new URLSearchParams(window.location.search);
    const variantParam = urlParams.get("variant");

    let selectedOption = null;
    let selectedVariantId = null;

    // 1. Priorität: URL-Parameter
    if (variantParam) {
      selectedOption = this.querySelector(
        `[data-variant-id="${variantParam}"]`
      );
      if (selectedOption && !selectedOption.disabled) {
        selectedVariantId = variantParam;
      } else {
        selectedOption = null; // Reset if disabled
      }
    }

    // 2. Priorität: default_variant_id Setting (nur wenn kein URL-Parameter)
    if (!selectedOption) {
      const defaultVariantId = this.dataset.defaultVariantId;
      if (defaultVariantId) {
        selectedOption = this.querySelector(
          `[data-variant-id="${defaultVariantId}"]`
        );
        if (selectedOption && !selectedOption.disabled) {
          selectedVariantId = defaultVariantId;
        } else {
          selectedOption = null; // Reset if disabled
        }
      }
    }

    // Wenn eine spezifische Variante gefunden wurde (URL oder Setting)
    if (selectedOption && selectedVariantId) {
      const paymentType = selectedOption.dataset.paymentType;
      const hasToggle = this.paymentTypeToggle !== null;

      if (hasToggle) {
        this.showPaymentType(paymentType);
        this.paymentTypeToggle.checked = paymentType === "onetime";
      }

      if (selectedOption.tagName === "OPTION") {
        const selectEl = selectedOption.closest("select");
        if (selectEl) {
          // Finde die passende Option über data-variant-id, NICHT über value
          Array.from(selectEl.options).forEach((opt) => {
            if (opt.dataset.variantId === selectedVariantId) {
              opt.selected = true;
              selectEl.dispatchEvent(new Event("change", { bubbles: true }));
            }
          });
        }
      } else {
        selectedOption.checked = true;
      }

      this.currentVariantId = selectedVariantId;
    }

    // Fallback: Wenn keine spezifische Variante gefunden wurde, verwende Standard-Logik
    if (!selectedOption) {
      const hasToggle = this.paymentTypeToggle !== null;

      if (!hasToggle) {
        // Kein Toggle - alle Optionen sind sichtbar, aktiviere die erste verfügbare (nicht disabled)
        const firstOption = this.querySelector(
          '[name="selling_plan"]:not(:disabled)'
        );
        if (firstOption) {
          firstOption.checked = true;
        }
      } else {
        // Toggle vorhanden - verwende show_first Setting
        // Bestimme Standard-Payment-Type basierend auf Toggle-Status
        const isOnetimeFirst = this.paymentTypeToggle.checked;
        const defaultPaymentType = isOnetimeFirst ? "onetime" : "subscription";

        // Zeige entsprechenden Payment-Type
        this.showPaymentType(defaultPaymentType);

        // Aktiviere die erste verfügbare (nicht disabled) Option des gewählten Typs
        // ABER: Kein automatisches Fallback auf anderen Payment-Type
        const firstOption = this.querySelector(
          `[data-payment-type="${defaultPaymentType}"]:not(:disabled)`
        );
        if (firstOption) {
          firstOption.checked = true;
        }
        // Wenn keine Option verfügbar ist, bleibt alles unchecked
        // checkAvailabilityState() wird dann den ATC Button disablen
      }
    }

    this.updatePriceAndSavings();

    // Update Toggle Labels nur wenn Toggle vorhanden
    if (this.paymentTypeToggle) {
      this.updatePaymentTypeLabels();
    }

    // Check if any options are available, if not disable add to cart
    this.checkAvailabilityState();
  }

  checkAvailabilityState() {
    const hasToggle = this.paymentTypeToggle !== null;
    let hasAvailableOptions = false;

    if (!hasToggle) {
      // Kein Toggle - prüfe alle verfügbaren Optionen
      hasAvailableOptions =
        this.querySelector('[name="selling_plan"]:not(:disabled)') !== null;
    } else {
      // Toggle vorhanden - prüfe nur den aktuell sichtbaren Payment-Type
      const isOnetimeActive = this.paymentTypeToggle.checked;
      const currentPaymentType = isOnetimeActive ? "onetime" : "subscription";

      // Prüfe nur Optionen des aktuell aktiven Payment-Types
      hasAvailableOptions =
        this.querySelector(
          `[data-payment-type="${currentPaymentType}"]:not(:disabled)`
        ) !== null;
    }

    if (!hasAvailableOptions) {
      // Hide add to cart button and show unavailable button
      if (this.addToCartButton) {
        this.addToCartButton.style.display = "none";
      }
      if (this.unavailableButton) {
        this.unavailableButton.style.display = "flex";
      }

      // Hide price display or show unavailable message
      if (this.priceElement) {
        this.priceElement.textContent = "—";
      }

      // Hide savings display
      if (this.savingsValue) {
        this.savingsValue.textContent = "";
      }

      // Show unavailable message (only if element exists in DOM)
      if (this.unavailableMessage) {
        this.unavailableMessage.style.display = "block";
      }

      // Hide cancel element when no options available
      if (this.cancelElement) {
        this.cancelElement.style.display = "none";
      }

      // Hide price wrapper and original price
      if (this.priceWrap) {
        this.priceWrap.style.display = "none";
      }
      if (this.originalPrice) {
        this.originalPrice.style.display = "none";
      }
    } else {
      // Show add to cart button and hide unavailable button
      if (this.addToCartButton) {
        this.addToCartButton.style.display = "block";
      }
      if (this.unavailableButton) {
        this.unavailableButton.style.display = "none";
      }

      // Hide unavailable message only if not permanently unavailable
      if (this.unavailableMessage) {
        this.unavailableMessage.style.display = "none";
      }

      // Show cancel element when options are available
      if (this.cancelElement) {
        this.cancelElement.style.display = "block";
      }

      // Show price wrapper and original price
      if (this.priceWrap) {
        this.priceWrap.style.display = "block";
      }
      if (this.originalPrice) {
        this.originalPrice.style.display = "block";
      }
    }
  }

  handleUrlChange() {
    // Prüfe URL-Parameter für neue Varianten-Auswahl
    const urlParams = new URLSearchParams(window.location.search);
    const variantParam = urlParams.get("variant");

    if (variantParam) {
      const targetOption = this.querySelector(
        `[data-variant-id="${variantParam}"]`
      );

      if (targetOption && !targetOption.checked && !targetOption.disabled) {
        // Deaktiviere alle anderen Optionen
        this.querySelectorAll('[name="selling_plan"]').forEach((input) => {
          input.checked = false;
        });

        // Bestimme Payment-Type und zeige entsprechenden Bereich
        const paymentType = targetOption.dataset.paymentType;
        const hasToggle = this.paymentTypeToggle !== null;

        if (hasToggle) {
          this.showPaymentType(paymentType);
          if (this.paymentTypeToggle) {
            this.paymentTypeToggle.checked = paymentType === "onetime";
          }
          this.updatePaymentTypeLabels();
        }

        // Aktiviere die neue Option
        targetOption.checked = true;
        this.currentVariantId = variantParam;

        // Update Preise und Anzeige
        this.updatePriceAndSavings();
      }
    }
  }

  handleSellingPlanChange(e) {
    let selectedElement;

    if (e.target.tagName === "SELECT") {
      // Dropdown: Hole das ausgewählte <option>
      selectedElement = e.target.options[e.target.selectedIndex];
    } else {
      // Radio: direkt der <input>
      selectedElement = e.target;
    }

    // aktuelle Variant-ID setzen
    this.currentVariantId = selectedElement.dataset.variantId;

    // Preis & Text aktualisieren
    this.updatePriceAndSavings();
    this.updateFrequencyLabels();
  }

  updatePriceAndSavings() {
    const checkedPlan =
      this.querySelector('[name="selling_plan"]:checked') ||
      this.querySelector("#selling-plan-select option:checked");

    if (!checkedPlan) return;

    const priceValue = parseFloat(checkedPlan.dataset.price);
    const compareAtPrice = parseFloat(checkedPlan.dataset.comparePrice);
    const planName = checkedPlan.dataset.name;
    const recurringDeliveries = checkedPlan.dataset.recurringDeliveries;

    // Preis anzeigen
    if (this.priceElement) {
      this.priceElement.textContent = formatMoney(priceValue, "{{amount}} €");
    }

    // Ersparnis berechnen und anzeigen
    this.updateSavingsDisplay(
      priceValue,
      compareAtPrice,
      planName,
      recurringDeliveries
    );
  }

  updateSavingsDisplay(
    priceValue,
    compareAtPrice,
    planName,
    recurringDeliveries
  ) {
    // Für Einmalzahlungen ist planName = "Einmalzahlung", für Abos verwenden wir Metafields
    // Daher können wir eine einfachere Logik verwenden
    const multiplier =
      recurringDeliveries === "false"
        ? 1
        : planName.match(/^(\d+)/)
        ? parseInt(planName.match(/^(\d+)/)[1])
        : 1;

    const savingsValue = (compareAtPrice - priceValue) * multiplier;

    // Savings-Anzeige (bleibt wie bisher)
    if (this.savingsValue) {
      if (recurringDeliveries === "false" || savingsValue <= this.threshold) {
        this.savingsValue.textContent = "";
      } else {
        this.savingsValue.textContent =
          "Du sparst " + formatMoney(savingsValue, "{{amount}} €");
      }
    }

    // Text-Anzeige-Logik (neue, getrennte Logik)
    this.updateTextDisplay(recurringDeliveries, planName);
  }

  updateTextDisplay(recurringDeliveries, planName) {
    const checkedPlan =
      this.querySelector('[name="selling_plan"]:checked') ||
      this.querySelector("#selling-plan-select option:checked");

    if (!checkedPlan) return;

    const variantTitle = (checkedPlan.dataset.variantTitle || "").toLowerCase();

    if (variantTitle.includes("flexibel")) {
      this.showTextElement("single"); // „Monatliche Zahlung, jederzeit kündbar.“
    } else if (
      variantTitle.includes("3 monate") ||
      variantTitle.includes("6 monate") ||
      variantTitle.includes("12 monate")
    ) {
      this.showTextElement("multiple"); // „Monatliche Zahlung, nach Laufzeit jederzeit kündbar.“
    } else if (variantTitle.includes("1 monat")) {
      this.showTextElement("onetime"); // „Einmalige Zahlung.“
    } else {
      // Fallback: falls nichts zutrifft
      this.showTextElement("onetime");
    }
  }

  showTextElement(type) {
    // Verstecke alle Text-Elemente
    if (this.multipleMonths) this.multipleMonths.style.display = "none";
    if (this.singleMonth) this.singleMonth.style.display = "none";
    if (this.onetimePurchase) this.onetimePurchase.style.display = "none";

    // Zeige das entsprechende Element
    switch (type) {
      case "multiple":
        if (this.multipleMonths) this.multipleMonths.style.display = "block";
        break;
      case "single":
        if (this.singleMonth) this.singleMonth.style.display = "block";
        break;
      case "onetime":
        if (this.onetimePurchase) this.onetimePurchase.style.display = "block";
        break;
    }
  }

  updateFrequencyLabels() {
    this.querySelectorAll(".product__selling-frequency").forEach((el) => {
      const sellingPlanInput = el
        .closest(".product__selling-plan-item")
        ?.querySelector('[name="selling_plan"]');
      if (
        sellingPlanInput &&
        sellingPlanInput.dataset.recurringDeliveries === "false"
      ) {
        el.textContent = " einmalig";
      }
    });
  }

  handlePaymentTypeChange(e) {
    const paymentType = e.target.checked ? "onetime" : "subscription";
    this.showPaymentType(paymentType);
    this.updatePaymentTypeLabels();

    // Deaktiviere alle aktuell gewählten Optionen
    this.querySelectorAll('[name="selling_plan"]').forEach((input) => {
      input.checked = false;
    });

    // Versuche die erste verfügbare Option des neuen Payment-Typs zu aktivieren
    const firstOption = this.querySelector(
      `[data-payment-type="${paymentType}"]:not(:disabled)`
    );
    if (firstOption) {
      firstOption.checked = true;
      this.updatePriceAndSavings();
    }

    // Wenn keine Option verfügbar ist, bleibt alles unchecked
    // checkAvailabilityState() wird den Button entsprechend handhaben
    this.checkAvailabilityState();
  }

  showPaymentType(type) {
    if (type === "onetime") {
      if (this.onetimeOptions) this.onetimeOptions.style.display = "flex";
      if (this.subscriptionOptions)
        this.subscriptionOptions.style.display = "none";
      if (this.paymentTypeToggle) this.paymentTypeToggle.checked = true;
    } else {
      if (this.onetimeOptions) this.onetimeOptions.style.display = "none";
      if (this.subscriptionOptions)
        this.subscriptionOptions.style.display = "flex";
      if (this.paymentTypeToggle) this.paymentTypeToggle.checked = false;
    }
  }

  updatePaymentTypeLabels() {
    this.querySelectorAll("[data-payment-type-label]").forEach((el, index) => {
      if (index === 0) {
        // Monatliche Zahlung Label (jetzt zuerst)
        el.classList.toggle(
          "text-weight--bold",
          this.paymentTypeToggle?.checked
        );
      } else {
        // Einmalzahlung Label (jetzt zweite)
        el.classList.toggle(
          "text-weight--bold",
          !this.paymentTypeToggle?.checked
        );
      }
    });
  }

  handleAddToCart(e) {
    e.preventDefault();

    const checkedPlan =
      this.querySelector('[name="selling_plan"]:checked') ||
      this.querySelector("#selling-plan-select option:checked");

    if (!checkedPlan) {
      console.error("Kein Selling Plan ausgewählt");
      return;
    }

    const variantId = checkedPlan.dataset.variantId;
    const sellingPlanId = checkedPlan.value || null; // Leer für Einmalzahlung
    const quantity = 1;

    const secondProductId =
      this.querySelector('[name="second_product_id"]')?.value || false;

    // Sammle alle Properties aus dem Formular
    const properties = this.collectFormProperties();

    this.addToCart(
      variantId,
      quantity,
      sellingPlanId,
      secondProductId,
      properties
    );
  }

  collectFormProperties() {
    const properties = {};
    const propertyInputs = this.subscriptionForm.querySelectorAll(
      'input[name^="properties["]'
    );

    propertyInputs.forEach((input) => {
      if (input.value.trim() !== "") {
        // Extrahiere Property-Namen aus name="properties[PropertyName]"
        const match = input.name.match(/properties\[(.+)\]/);
        if (match) {
          properties[match[1]] = input.value;
        }
      }
    });

    return properties;
  }

  addToCart(
    variantId,
    quantity,
    sellingPlanId,
    secondProductId,
    properties = {}
  ) {
    const cartData = {
      id: variantId,
      quantity: quantity,
    };

    // Nur Selling Plan hinzufügen, wenn einer ausgewählt ist
    if (sellingPlanId) {
      cartData.selling_plan = sellingPlanId;
    }

    // Properties hinzufügen, wenn vorhanden
    if (Object.keys(properties).length > 0) {
      cartData.properties = properties;
    }

    fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(cartData),
    })
      .then((response) => response.json())
      .then(() => {
        if (secondProductId) {
          // Zweites Produkt hinzufügen
          return fetch("/cart/add.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              id: secondProductId,
              quantity: 1,
            }),
          });
        } else {
          return Promise.resolve();
        }
      })
      .then((response) => {
        if (response) {
          return response.json();
        }
      })
      .then(() => {
        // Warenkorb öffnen
        const elm = document.querySelector(
          '[aria-controls="site-cart-sidebar"]'
        );
        const elmSidebar = document.getElementById("site-cart-sidebar");
        if (elm && elmSidebar) {
          elm.setAttribute("aria-expanded", "true");
          elmSidebar.show();
        }

        if (window.refreshCart) {
          window.refreshCart();
        }
      })
      .catch((error) => {
        console.error("Fehler beim Hinzufügen zum Warenkorb:", error);
      });

    // Mini-Cart Toggle Event
    dispatchEvent(
      new CustomEvent("mini-cart:toggle", {
        detail: {
          open: true,
        },
      })
    );
  }
}

customElements.define("subscription-selector", SubscriptionSelector);
